import {useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Input,
  Modal,
  Progress,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  InboxOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type {RcFile, UploadFile} from 'antd/lib/upload/interface';
import {SchoolService} from './SchoolService';
import type {SchoolImportStatus} from './SchoolService';

const {Dragger} = Upload;
const {Text} = Typography;

const schoolService = new SchoolService();

type Props = {
  open: boolean;
  onClose: () => void;
};

const terminalStatuses = new Set([
  'DONE',
  'FAILED',
  'COMPLETED',
  'SUCCESS',
  'PARTIALLY_FAILED',
]);

const isXlsxFile = (file: RcFile) => {
  const lower = file.name.toLowerCase();
  return (
    lower.endsWith('.xlsx') ||
    file.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

export const SchoolImportModal = ({open, onClose}: Props) => {
  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [s3Url, setS3Url] = useState('');
  const [importId, setImportId] = useState('');
  const [status, setStatus] = useState<SchoolImportStatus | null>(null);
  const [pollTick, setPollTick] = useState(0);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setUploading(false);
      setImporting(false);
      setStatusLoading(false);
      setS3Url('');
      setImportId('');
      setStatus(null);
      setPollTick(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !importId) {
      return;
    }
    if (status?.status && terminalStatuses.has(status.status.toUpperCase())) {
      return;
    }

    const intervalId = setInterval(() => {
      setPollTick(prev => prev + 1);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [open, importId, status?.status]);

  useEffect(() => {
    if (!open || !importId) {
      return;
    }

    fetchStatus(importId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollTick]);

  const progressPercent = useMemo(() => {
    if (!status?.totalSchools) return 0;
    return Math.min(
      100,
      Math.round((status.doneSchools / status.totalSchools) * 100),
    );
  }, [status]);

  const currentStep = useMemo(() => {
    if (!s3Url) return 0;
    if (s3Url && !importId) return 1;
    return 2;
  }, [s3Url, importId]);

  const fetchStatus = async (value: string, silent = false) => {
    if (!value) {
      return;
    }
    if (!silent) {
      setStatusLoading(true);
    }

    try {
      console.log('Fetching status for importId:', value);
      const resp = await schoolService.getImportStatus(value.trim());
      console.log('Fetching status for importId:', resp);

      setStatus(resp);
    } catch (error: any) {
      console.error('Error fetching import status:', error);
      if (!silent) {
        message.error(error?.message || 'Failed to fetch import status');
      }
    } finally {
      if (!silent) {
        setStatusLoading(false);
      }
    }
  };

  const handleUploadToS3 = async () => {
    if (!selectedFile) {
      message.warning('Please choose an .xlsx file first');
      return;
    }

    setUploading(true);
    try {
      const fileUrl = await schoolService.uploadImportFile(selectedFile as any);
      setS3Url(fileUrl);
      message.success('File uploaded successfully to S3');
    } catch (error: any) {
      message.error(error?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleStartImport = async () => {
    if (!s3Url) {
      message.warning('Upload the file first');
      return;
    }
    const normalized = s3Url.toLowerCase();
    if (
      !normalized.includes('uploads.') ||
      !normalized.includes('/public/') ||
      !normalized.endsWith('.xlsx')
    ) {
      message.error(
        'Invalid S3 URL. Expected uploads bucket URL under public/ and .xlsx extension.',
      );
      return;
    }

    setImporting(true);
    try {
      const id = await schoolService.importCsv(s3Url);
      setImportId(id);
      message.success('Import started successfully');
      await fetchStatus(id, true);
    } catch (error: any) {
      message.error(error?.message || 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const schoolColumns = [
    {title: 'School Name', dataIndex: 'name'},
    {title: 'Email', dataIndex: 'email'},
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: string) => {
        const normalized = (value || '').toUpperCase();
        const color =
          normalized === 'DONE' || normalized === 'SUCCESS'
            ? 'green'
            : normalized.includes('FAIL')
              ? 'red'
              : 'blue';
        return <Tag color={color}>{value || '-'}</Tag>;
      },
    },
    {
      title: 'Teachers',
      render: (_: any, row: any) => `${row.teachersDone}/${row.teacherCount}`,
    },
    {
      title: 'Classes',
      render: (_: any, row: any) => `${row.classesDone}/${row.classCount}`,
    },
    {
      title: 'Students',
      render: (_: any, row: any) => `${row.studentsDone}/${row.studentCount}`,
    },
    {
      title: 'Retry',
      render: (_: any, row: any) =>
        `${row.retryCount ?? 0}/${row.maxAttempts ?? 0}`,
    },
    {title: 'Failed Attempts', dataIndex: 'failedAttempts'},
    {title: 'Error', dataIndex: 'error', ellipsis: true},
  ];

  const validationColumns = [
    {title: 'Sheet', dataIndex: 'sheet'},
    {title: 'Row', dataIndex: 'row', width: 90},
    {
      title: 'Messages',
      dataIndex: 'messages',
      render: (messagesList: string[]) =>
        messagesList?.length ? messagesList.join(' | ') : '-',
    },
  ];

  return (
    <Modal
      title="Bulk Schools Import (.xlsx)"
      open={open}
      width={1100}
      onCancel={onClose}
      footer={null}
      destroyOnClose>
      <Alert
        type="info"
        showIcon
        message="Expected flow"
        description="1) Upload .xlsx to uploads.development.pikado.io/public/, 2) start importCsv, 3) monitor getImportStatus."
        style={{marginBottom: 16}}
      />

      <Steps
        current={currentStep}
        items={[
          {title: 'Select File'},
          {title: 'Upload To S3'},
          {title: 'Import & Track'},
        ]}
      />

      <Divider />

      <Dragger
        maxCount={1}
        fileList={selectedFile ? [selectedFile] : []}
        beforeUpload={(file: RcFile) => {
          if (!isXlsxFile(file)) {
            message.error('Only .xlsx files are allowed');
            return Upload.LIST_IGNORE;
          }
          setSelectedFile(file as any);
          return false;
        }}
        onRemove={() => {
          setSelectedFile(null);
          setS3Url('');
          return true;
        }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Drag and drop `.xlsx` file or click to choose
        </p>
        <p className="ant-upload-hint">
          Validation: file must end with `.xlsx`.
        </p>
      </Dragger>

      <Space style={{marginTop: 14}}>
        <Button
          icon={<UploadOutlined />}
          type="primary"
          loading={uploading}
          onClick={handleUploadToS3}
          disabled={!selectedFile}>
          Upload To S3
        </Button>
        <Button
          icon={<PlayCircleOutlined />}
          type="default"
          loading={importing}
          onClick={handleStartImport}
          disabled={!s3Url}>
          Start Import
        </Button>
      </Space>

      {!!s3Url && (
        <Alert
          type="success"
          showIcon
          style={{marginTop: 12}}
          message="File uploaded to S3"
          description={
            <Text copyable={{text: s3Url}} style={{wordBreak: 'break-all'}}>
              {s3Url}
            </Text>
          }
        />
      )}

      <Divider />

      <Space align="start" style={{width: '100%'}}>
        <Input
          placeholder="Paste importId to track existing import"
          value={importId}
          onChange={e => setImportId(e.target.value)}
          style={{width: 420}}
        />
        <Button loading={statusLoading} onClick={() => fetchStatus(importId)}>
          Get Status
        </Button>
      </Space>

      {(statusLoading || importing) && (
        <div style={{textAlign: 'center', padding: 20}}>
          <Spin />
        </div>
      )}

      {status && (
        <>
          <div style={{marginTop: 16}}>
            <Progress
              percent={progressPercent}
              status={progressPercent === 100 ? 'success' : 'active'}
            />
          </div>

          <Descriptions
            bordered
            size="small"
            column={2}
            style={{marginTop: 12}}>
            <Descriptions.Item label="Import ID">
              {status.importId}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  terminalStatuses.has((status.status || '').toUpperCase())
                    ? 'green'
                    : 'blue'
                }>
                {status.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Schools">
              {status.totalSchools ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Done">
              {status.doneSchools ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Failed Attempts">
              {status.failedAttempts ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Failed Schools">
              {status.failedSchools ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {status.updatedAt ?? '-'}
            </Descriptions.Item>
          </Descriptions>

          {!!status.validationErrors?.length && (
            <>
              <Divider orientation="left">Validation Errors</Divider>
              <Table
                rowKey={(_, index) => `validation-error-${index}`}
                size="small"
                pagination={{pageSize: 5}}
                columns={validationColumns as any}
                dataSource={status.validationErrors}
              />
            </>
          )}

          <Divider orientation="left">Schools</Divider>
          <Table
            rowKey={(row: any) => row.tempId || row.schoolId || row.email}
            size="small"
            pagination={{pageSize: 8}}
            columns={schoolColumns as any}
            dataSource={status.schools ?? []}
          />
        </>
      )}
    </Modal>
  );
};
